<template>
  <div class="mr-4 ml-4">
    <div class="row">
      <div class="col-12">
        <page-header
          class="page-header-family-text"
          :actions="headerActionsConfig"
          left-side-class="col-12 col-md-7"
          :page="headerConfig"
          right-side-class="col-12 col-md-5 d-flex mt-5"
        />
      </div>
    </div>
    <div v-if="readOnly" class="col-12">
      <relate-number-sie
        :information="registrationInfo"
        @reload-info="getOnlineRegistration"
      />
    </div>
    <demographic-information
      ref="demographicRef"
      v-model="model.demographic"
      :registration-number="registrationInfo?.registrationNumber"
      :centers="collections.centers"
      :genders="collections.genders"
      :ethnicities="collections.ethnicities"
      :readOnly="readOnly"
      @change-center="changeCenterHandler"
    />
    <list-of-registration-relationship
      ref="relationshipRef"
      :list="listOfRelationship"
      :relationships="collections.relationships"
      :readOnly="readOnly"
      @input="setRelationship"
    />
    <address-information
      v-model="model.addresses"
      :registration-id="registrationId"
      :ref="addressRef"
      :municipalities="collections.municipalities"
      :readOnly="readOnly"
      @refresh="getOnlineRegistration"
    />
    <documents-information
      :documents="model.documents"
      :readOnly="readOnly"
      @download="downloadDocument"
      @upload="uploadDocument"
    />
  </div>
</template>

<script>
//third party libs
import _ from "lodash";

// Components
import PageHeader from "PageHeader";
import DemographicInformation from "./RegistrationDemographicInformation";
import AddressInformation from "./RegistrationAddressInformation";
import DocumentsInformation from "./RegistrationDocumentsInformation";
import RelateNumberSie from "./RelateNumberSie";
import ListOfRegistrationRelationship from "./ListOfRegistrationRelationship.vue";

// Services
import StudentServices from "@/services/student";
import DocumentServices from "@/services/documents";

// Utils
import alertMixin from "@quisit/mipe-utils/mixins/alert.mixin";
import registrationStatuses from "@/utils/constants/publicPortalRegistrationStatuses.js";
import objectToFormData from "object-to-formdata";

export default {
  name: "registrationDetail",
  mixins: [alertMixin],
  components: {
    PageHeader,
    DemographicInformation,
    AddressInformation,
    DocumentsInformation,
    RelateNumberSie,
    ListOfRegistrationRelationship
  },
  computed: {
    headerConfig() {
      return {
        title: "Ver Solicitud",
        icon: "fas fa-user-circle",
        routes: ["Inicio", "Estudiante", "Comenzar Proceso de Solicitud"]
      };
    },
    headerActionsConfig() {
      return [
        {
          text: "Regresar",
          icon: "fas fa-arrow-circle-left pr-1 ",
          variant: "outline-success",

          click: () => {
            this.$router.push({ name: "referralPartCToPartB" });
          }
        },
        {
          text: "Guardar",
          icon: "fas fa-save pr-1",
          variant: "success",
          visible: !this.readOnly,
          click: () => {
            this.save();
          }
        },
        {
          text: "Someter",
          icon: "fas fa-flag pr-1",
          variant: "success",
          visible: !this.readOnly,
          click: () => {
            this.submit();
          }
        }
      ];
    },
    registrationId() {
      return this.$route.params?.registrationId
        ? Number(this.$route.params?.registrationId)
        : null;
    },
    readOnly() {
      const statuses = [
        registrationStatuses.related,
        registrationStatuses.submitted,
        registrationStatuses.completed
      ];

      return (
        this.registrationId &&
        statuses.includes(this.registrationInfo?.registrationStatusId)
      );
    },
    addressRef() {
      return "addressRef";
    }
  },
  data() {
    return {
      collections: {},
      registrationInfo: null,
      listOfRelationship: [],
      model: {
        demographic: {
          center: null,
          birthDate: null,
          gender: null,
          ethnicity: null,
          ssn: null,
          firstName: null,
          initial: null,
          lastPaternalName: null,
          lastMaternalName: null
        },
        relationship: [],
        addresses: {
          physical: {
            addressLine1: null,
            addressLine2: null,
            municipality: null,
            zipcode: null
          },
          postal: {
            addressLine1: null,
            addressLine2: null,
            municipality: null,
            zipcode: null
          }
        },
        documents: []
      }
    };
  },
  async mounted() {
    await this.getCatalog();
    await this.getOnlineRegistration();
  },
  methods: {
    async getCatalog() {
      const { data } = await StudentServices.getOnlineRegistrationCatalog();

      this.collections = {
        centers: data.centers,
        genders: data.genders,
        ethnicities: data.ethnicities,
        relationships: data.relationships,
        municipalities: data.municipalities
      };
    },
    async getOnlineRegistration() {
      const { data } = await StudentServices.getOnlineRegistration(
        this.registrationId
      );

      this.registrationInfo = data;

      if (data) {
        this.model.demographic = {
          center: data.center,
          birthDate: data.birthDate,
          gender: data.gender,
          ethnicity: data.ethnicity,
          ssn: data.ssn,
          firstName: data.firstName,
          initial: data.initialName,
          lastPaternalName: data.lastPaternalName,
          lastMaternalName: data.lastMaternalName
        };
        this.listOfRelationship = data.relationships;
        this.model.addresses = {
          physical: {
            addressLine1: data.physicalAddressLine1,
            addressLine2: data.physicalAddressLine2,
            municipality: data.physicalMunicipality,
            zipcode: data.physicalAddressZipCode
          },
          postal: {
            addressLine1: data.postalAddressLine1,
            addressLine2: data.postalAddressLine2,
            municipality: data.postalMunicipality,
            zipcode: data.postalAddressZipCode
          }
        };
        this.model.documents = data.documents;

        this.$refs[this.addressRef].initialPostalValue = {
          ...this.model.addresses.postal
        };
      }
    },
    async getOnlineRegistrationDocuments() {
      const { data } = await StudentServices.getOnlineRegistrationDocuments(
        this.registrationId
      );
      this.model.documents = data;
    },
    async downloadDocument(item) {
      if (item?.documentId) {
        await DocumentServices.dowloadPublicPortalDocumentByDocumentId(
          item.documentId
        );
      }
    },
    async uploadDocument(item, file) {
      if (item) {
        const payload = objectToFormData(
          {
            registrationId: this.registrationId,
            documentId: item.documentId,
            documentTypeId: item.documentTypeId,
            file
          },
          { indices: true, nullsAsUndefineds: true }
        );
        await StudentServices.uploadPublicPortalDocument(payload);
        await this.getOnlineRegistrationDocuments();

        if (item.documentId) {
          this.ShowToast(
            "",
            "El documento ha sido reemplazado exitosamente.",
            "success"
          );
        }
      }
    },
    getModelPayload(submitRequest = false) {
      const { demographic, relationship, addresses } = this.model;

      const payload = {
        id: this.registrationId,
        submitRequest: submitRequest,

        //demographic info
        centerId: demographic.center?.id,
        birthDate: demographic.birthDate,
        genderId: demographic.gender?.id,
        ethnicityId: demographic.ethnicity?.id,
        ssn: _.replace(demographic.ssn, new RegExp("-", "g"), ""),
        firstName: demographic.firstName,
        initialName: demographic.initial,
        lastPaternalName: demographic.lastPaternalName,
        lastMaternalName: demographic.lastMaternalName,

        //relationship
        relationships: relationship.map(e => ({
          Id: e.id,
          RegistrationId: e.registrationId,
          Name: e.name,
          LastName: e.lastName,
          Email: e.email,
          Phone1: e.phone1,
          Phone2: e.phone2,
          RelationShipTypeId: e.relationshipType.id
        })),

        //physical address
        physicalAddressLine1: addresses.physical.addressLine1,
        physicalAddressLine2: addresses.physical.addressLine2,
        physicalAddressMunicipalityId: addresses.physical.municipality?.id,
        physicalAddressZipCode: addresses.physical.zipcode,

        //postal address
        postalAddressLine1: addresses.postal.addressLine1,
        postalAddressLine2: addresses.postal.addressLine2,
        postalAddressMunicipalityId: addresses.postal.municipality?.id,
        postalAddressZipCode: addresses.postal.zipcode
      };
      return payload;
    },
    async allFieldsAreValidated() {
      const isDemographicValid = await this.$refs.demographicRef.$refs.demographicForm.validate();
      const isRelationshipValid = await this.$refs.relationshipRef.validate();
      const isPhysicalAddressValid = await this.$refs.addressRef.$refs.physicalForm.validate();
      const isPostalAddressValid = await this.$refs.addressRef.$refs.postalForm.validate();

      return (
        isDemographicValid &&
        isRelationshipValid &&
        isPhysicalAddressValid &&
        isPostalAddressValid
      );
    },
    async save() {
      const isValid = await this.allFieldsAreValidated();
      if (!isValid) {
        this.ShowToast(
          "Alerta",
          "Favor completar la información solicitada.",
          "error"
        );
        return;
      }

      const response = await this.ShowCancelModal(
        "Confirmación",
        "¿Está seguro(a) que desea guardar la información presentada?"
      );
      if (!response) return;

      const payload = this.getModelPayload();

      const { data } = await StudentServices.saveOrSubmitOnlineRegRequest(
        payload
      ).catch(err =>
        this.ShowToast("", err.message || err.response.data.message, "error")
      );

      if (data) {
        this.ShowToast(
          "Proceso exitoso",
          "La información ha sido guardada exitosamente.",
          "success"
        );
        await this.getOnlineRegistration();
      }
    },
    async submit() {
      const isValid = await this.allFieldsAreValidated();

      if (!isValid) {
        this.ShowToast(
          "Alerta",
          "Favor completar la información solicitada.",
          "error"
        );
        return;
      }
      const answer = await this.ShowCancelModal(
        "Confirmación",
        "¿Está seguro(a) que desea someter la información presentada?",
        "Sí",
        "No"
      );
      if (!answer) return;

      let payload = this.getModelPayload(true);

      const { data } = await StudentServices.saveOrSubmitOnlineRegRequest(
        payload
      ).catch(err =>
        this.ShowToast("", err.message || err.response.data.message, "error")
      );

      if (data) {
        this.ShowToast(
          "Proceso exitoso",
          "La información ha sido sometida exitosamente.",
          "success"
        );
        await this.getOnlineRegistration();
      }
    },
    setRelationship(listOfRelationship) {
      this.model.relationship = [...listOfRelationship];
    },
    async changeCenterHandler(centerId){      
      if(this.registrationId)
      {
        await StudentServices.changeCenter(this.registrationId, centerId);        
      }
      
    }
  }
};
</script>

<style></style>
